import Dialog from "../../General/ConfirmableDialog";
import React, { useState, ChangeEvent } from "react";
import {
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  FormControl,
  Box,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { IFileUploadModalProps } from "../../../interfaces/Fleet/IFileUploadModalProps";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMsal } from "@azure/msal-react";

const FileUploadModal: React.FC<IFileUploadModalProps> = ({
  open,
  onClose,
  row,
  type,
  incident,
  employeeName,
  employeeFileCategory,
  employeeDocumentDate,
  unitNumber,
  assetFileType,
  companyId,
  startDate,
  endDate,
  overwriteExisting = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [moneyRequestStatus, setMoneyRequestStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [moneyRequestErrorMessage, setMoneyRequestErrorMessage] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState<Dayjs | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState<string | null>("");
  const [referenceId, setReferenceId] = useState<string>("");

  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { accounts } = useMsal();
  const addedBy = accounts[0]?.name;
  const handleSnackbarClose = () => setStatus("idle");
  const handleMoneyRequestSnackbarClose = () => setMoneyRequestStatus("idle");

  const uploadFileMutation = useMutation({
    mutationFn: async () => {
      if (!file || !row) {
        setStatus("error");
        setErrorMessage("File not provided or row data is missing");
        setLoading(false);
        throw new Error("File not provided or row data is missing");
      }

      const formData = new FormData();
      formData.append("file", file);

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        params: {
          ...(type === "invoice"
            ? {
              invoiceDate: invoiceDate?.format("YYYY-MM-DD"),
              invoiceNumber,
              overwriteExisting: overwriteExisting,
            }
            : type === "personalFolder"
              ? {
                employeeName: employeeName,
                employeeFileCategory: employeeFileCategory,
                documentDate: employeeDocumentDate,
                overwriteExisting,
              }
              : type === "assetFolder"
                ? {
                  unitNumber: unitNumber,
                  assetDocumentType: assetFileType,
                  assetDocumentStartDate: startDate?.format("YYYY-MM-DD"),
                  assetDocumentExpirationDate: endDate?.format("YYYY-MM-DD"),
                  rentCompanyId: companyId,
                  assetDocumentAddedBy: addedBy,
                  overwriteExisting,
                }
                : { overwriteExisting }),
        },
      };
      const response = await axios.post(
        `${apiVariable}/upload/${row.id}/${type}`,
        formData,
        config
      );
      return response.data;
    },
    onSuccess: () => {
      setStatus("success");
      setLoading(false);
      if (row) onClose(row);

      setFile(null);
      setInvoiceDate(null);
      setInvoiceNumber("");
      setReferenceId("");
      if (row) onClose(row);
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to upload file");
      setLoading(false);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const uploadAndCreateMaintenanceHistoryMutation = useMutation({
    mutationFn: async () => {
      if (!file || !row || !incident?.truckId) {
        setStatus("error");
        setErrorMessage("File, row data, or incident truck ID is missing");
        setLoading(false);
        throw new Error("File, row data, or incident truck ID is missing");
      }

      const formData = new FormData();
      formData.append("file", file);

      const maintenanceHistoryDto = {
        maintenanceOperationId: null,
        notes: `Generated by system with invoice number: ${invoiceNumber}`,
      };

      Object.entries(maintenanceHistoryDto).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string);
        }
      });

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        params: {
          overwriteExisting: overwriteExisting,
        },
      };

      const response = await axios.post(
        `${apiVariable}/uploadAndCreateMaintenanceHistory/maintenance/${incident.truckId}`,
        formData,
        config
      );
      return response.data;
    },
    onSuccess: () => {
      setStatus("success");
      setLoading(false);
      if (row) onClose(row);

      setFile(null);
      setInvoiceDate(null);
      setInvoiceNumber("");
      setReferenceId("");
      queryClient.invalidateQueries({ queryKey: ["maintenanceHistories"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(
        error.response.data || "Failed to upload and create maintenance history"
      );
      setLoading(false);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    // Generate money request for EFS payment method
    if (row?.paymentMethod === "efs" && type === "invoice" && incident) {
      try {
        const formData = new FormData();
        formData.append("file", file as Blob);

        formData.append("driverId", incident?.driverId);
        try {
          // Get truckId from incident if it exists, otherwise get it from trailerId
          if (incident?.truckId) {
            formData.append("truckId", incident?.truckId);
          } else {
            const response = await axios.get(
              `${apiVariable}/getTruckFromTrailer/${incident?.trailerId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            const truckId = response.data.id;
            formData.append("truckId", truckId);
          }
          setMoneyRequestStatus("success");
        } catch {
          setMoneyRequestStatus("error");
          setMoneyRequestErrorMessage(
            "Money request could not be generated automatically, please add it manually"
          );
          // If truckId is not found, set status to error and upload file only in repair
          uploadFileMutation.mutate();
        }

        formData.append(
          "amount",
          row?.finalCost !== 0
            ? row?.finalCost?.toString()
            : incident?.finalCost?.toString()
        );
        formData.append("moneyRequestType", "Repair");
        formData.append(
          "description",
          `Repair request generated automatically by the system with invoice number: ${invoiceNumber ?? ""}`
        );
        formData.append("usernameRequestedBy", "System");
        formData.append("nameRequestedBy", "System");
        formData.append("dateRequested", new Date().toISOString());
        formData.append("status", "Approved");
        formData.append("requiresApproval", "false");
        formData.append("completedWithoutApproval", "false");
        if (referenceId.trim()) {
          formData.append("referenceId", referenceId.trim());
        }

        const moneyRequestResponse = await axios.post(
          `${apiVariable}/duplicateFile/moneyRequestInvoice`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
            params: {
              overwriteExisting: overwriteExisting,
            },
          }
        );
        const moneyRequestId = moneyRequestResponse.data?.id;
        // Create maintenance history for PM service if incident type is pmService

        if (incident.incidentType === "pmService") {
          uploadAndCreateMaintenanceHistoryMutation.mutate();
        }

        setStatus("success");
        setMoneyRequestStatus("success");
        // Upload file in repair
        uploadFileMutation.mutate();
        queryClient.invalidateQueries({ queryKey: ["repairs", incident.id] });
        if (row) onClose({ ...row, moneyRequestId });
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(
          error.response?.data || "Failed to upload and duplicate file"
        );
      }
    } else if (row?.paymentMethod === "efs" && type === "receipt") {
      if (row?.moneyRequestId) {
        // We DO have an ID. Upload normally.
        if (!file) throw new Error("Receipt file is required");

        const receiptFormData = new FormData();
        receiptFormData.append("file", file);

        await axios.post(
          `${apiVariable}/upload/${row.moneyRequestId}/moneyRequestReceipt`,
          receiptFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setStatus("success");
        queryClient.invalidateQueries({ queryKey: ["repairs", row?.id] });
        queryClient.invalidateQueries({ queryKey: ["moneyRequests"] });
        // Also upload the file in repair if needed:
        uploadFileMutation.mutate();
        onClose(row);
      } else {
        // We do NOT have a moneyRequestId.
        if (!file) throw new Error("Receipt file is required");

        uploadFileMutation.mutate();
        setStatus("success");
        queryClient.invalidateQueries({ queryKey: ["repairs", row?.id] });
        onClose(row);
      }
    }

    // Upload file in repair and create maintenance history for PM service
    else if (
      type === "invoice" &&
      incident?.truckId &&
      incident?.incidentType === "pmService"
    ) {
      uploadAndCreateMaintenanceHistoryMutation.mutate();
      uploadFileMutation.mutate();
      queryClient.invalidateQueries({ queryKey: ["repairs", incident.id] });
    }
    // Upload file only in repair
    else {
      uploadFileMutation.mutate();
      queryClient.invalidateQueries({ queryKey: ["moneyRequests"] });
      queryClient.invalidateQueries({
        queryKey: ["needingApprovalRequests"],
      });
      queryClient.invalidateQueries({ queryKey: ["allRequests"] });
    }

    setLoading(false);
  };
  const isSubmitDisabled = () => {
    if (loading) return true;
    if (type === "invoice") {
      return !invoiceDate || !invoiceNumber;
    }
    return false;
  };

  const shouldShowReferenceIdField =
    type === "invoice" &&
    row?.paymentMethod?.toLowerCase?.() === "efs" &&
    row?.repairStatus?.toLowerCase?.() === "completed" &&
    Boolean(incident);

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Upload {camelCaseToSentenceCase(type)}</DialogTitle>
        <DialogContent>
          <Box sx={{ marginBottom: 2 }}>
            <FormControl fullWidth>
              <TextField
                type="file"
                onChange={handleFileChange}
                variant="outlined"
                inputProps={{
                  accept: "application/pdf, image/jpeg, image/png",
                }}
              />
              {type === "invoice" && (
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Invoice Date"
                      value={invoiceDate}
                      onChange={(newValue) => {
                        newValue && setInvoiceDate(newValue);
                      }}
                      slotProps={{ textField: { variant: "outlined" } }}
                      sx={{
                        marginTop: 2,
                      }}
                    />
                  </LocalizationProvider>
                  <TextField
                    label="Invoice Number"
                    variant="outlined"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    sx={{
                      marginTop: 2,
                    }}
                  />
                  {shouldShowReferenceIdField && (
                    <TextField
                      label="Reference ID"
                      variant="outlined"
                      value={referenceId}
                      onChange={(e) => setReferenceId(e.target.value)}
                      inputProps={{ maxLength: 50 }}
                      sx={{
                        marginTop: 2,
                      }}
                    />
                  )}
                </>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => onClose(onClose)}
            color="secondary"
            variant="outlined"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitDisabled()}
          >
            {loading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      <StatusSnackbar
        status={moneyRequestStatus}
        successMessage={"Money request generated successfully"}
        errorMessage={moneyRequestErrorMessage}
        handleClose={handleMoneyRequestSnackbarClose}
        sx={{
          marginBottom: 7,
        }}
      />
      <StatusSnackbar
        status={status}
        successMessage={`${type.charAt(0).toUpperCase() + type.slice(1)
          } uploaded successfully`}
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default FileUploadModal;
